import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import { ENDPOINTS } from "@/api/application-api";
import type { StructuredFilterGroupRequest } from "@/components/StructuredSearch";
import {
  getFilterDefinitionByKey,
  SuggestionSourceType,
  type StructuredSearchSchema,
  type ValueSuggestionsHookResult,
} from "@/components/StructuredSearch/config/structured-search-schema";
import { getFilterSuggestionConfig } from "@/components/StructuredSearch/config/suggestion-config";
import { StructuredSearchSuggestionField } from "@/components/StructuredSearch/config/types";
import { STRUCTURED_SEARCH_QUERY_CONFIG } from "@/components/StructuredSearch/config/query-config";
import { mapStructuredFilterGroupToBackend } from "@/queries/request-log-query";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { useUser } from "@/context/user-context";
import { useUserToken } from "@/context/auth-context";
import { useDebounce } from "@/hooks";
import { queryKeys } from "@/constants";

const {
  SUGGESTION_DEBOUNCE_MS,
  REGISTRY_SUGGESTION_DEBOUNCE_MS,
  QUERY_STALE_MS,
  QUERY_RETRY_COUNT,
} = STRUCTURED_SEARCH_QUERY_CONFIG;

const BACKEND_SOURCE_BY_TYPE: Record<SuggestionSourceType, string> = {
  [SuggestionSourceType.Static]: "static",
  [SuggestionSourceType.Aggregation]: "aggregation",
  [SuggestionSourceType.Nested]: "aggregation",
  [SuggestionSourceType.PromptRegistry]: "prompt_registry",
  [SuggestionSourceType.WorkflowRegistry]: "workflow_registry",
};

export interface StructuredSearchSuggestionsArgs {
  schema: StructuredSearchSchema;
  field: string | undefined;
  input: string;
  enabled: boolean;
  suggestionField: string;
  metadataKey?: string;
  promptId?: number;
  workflowId?: number;
  filterGroup?: StructuredFilterGroupRequest;
  entitySelectStep: boolean;
  entityVersionStep?: boolean;
  hideSignalMetadata?: boolean;
  /** When set, sent as the API prefix instead of the typed input. */
  prefixOverride?: string;
}

interface StructuredSearchSuggestionsResponse {
  values: {
    value: string;
    count?: number;
    prompt_name?: string | null;
    label?: string | null;
  }[];
}

const serializeFilterGroup = (filterGroup?: StructuredFilterGroupRequest): string | undefined => {
  const backendFilterGroup = mapStructuredFilterGroupToBackend(filterGroup);
  return backendFilterGroup ? JSON.stringify(backendFilterGroup) : undefined;
};

const resolveSuggestionSource = (
  schema: StructuredSearchSchema,
  field: string | undefined,
  entitySelectStep: boolean,
  entityVersionStep: boolean,
): SuggestionSourceType | null => {
  if (entitySelectStep || entityVersionStep) {
    return SuggestionSourceType.Aggregation;
  }

  const definition = getFilterDefinitionByKey(schema, field);
  const suggestionConfig = getFilterSuggestionConfig(definition);
  return suggestionConfig?.source ?? null;
};

const resolveBackendSource = (sourceType: SuggestionSourceType | null): string | null => {
  if (!sourceType || sourceType === SuggestionSourceType.Static) {
    return null;
  }

  return BACKEND_SOURCE_BY_TYPE[sourceType];
};

const resolveAggregationField = ({
  schema,
  field,
  suggestionField,
  entitySelectStep,
  entityVersionStep,
}: {
  schema: StructuredSearchSchema;
  field: string | undefined;
  suggestionField: string;
  entitySelectStep: boolean;
  entityVersionStep: boolean;
}): string => {
  if (entitySelectStep) {
    return StructuredSearchSuggestionField.Prompt;
  }

  if (entityVersionStep) {
    return suggestionField;
  }

  const definition = getFilterDefinitionByKey(schema, field);
  const suggestionConfig = getFilterSuggestionConfig(definition);
  if (suggestionConfig?.source === SuggestionSourceType.Aggregation && suggestionConfig.field) {
    return suggestionConfig.field;
  }

  return suggestionField;
};

export const useStructuredSearchSuggestions = ({
  schema,
  field,
  input,
  enabled,
  suggestionField,
  metadataKey,
  promptId,
  workflowId,
  filterGroup,
  entitySelectStep,
  entityVersionStep = false,
  hideSignalMetadata,
  prefixOverride,
}: StructuredSearchSuggestionsArgs): ValueSuggestionsHookResult => {
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const userToken = useUserToken();
  const suggestionSourceType = resolveSuggestionSource(
    schema,
    field,
    entitySelectStep,
    entityVersionStep,
  );
  const backendSource = resolveBackendSource(suggestionSourceType);
  const debounceMs =
    suggestionSourceType === SuggestionSourceType.PromptRegistry ||
    suggestionSourceType === SuggestionSourceType.WorkflowRegistry
      ? REGISTRY_SUGGESTION_DEBOUNCE_MS
      : SUGGESTION_DEBOUNCE_MS;
  const debouncedInput = useDebounce(input, debounceMs);
  // Empty input (field/key select) must fetch immediately — don't wait for debounce effect.
  const typedPrefix = input.trim() === "" ? "" : debouncedInput.trim();
  const prefix = prefixOverride ?? typedPrefix;
  const aggregationField = resolveAggregationField({
    schema,
    field,
    suggestionField,
    entitySelectStep,
    entityVersionStep,
  });
  const serializedFilterGroup = serializeFilterGroup(filterGroup);
  const suggestionFiltersKey = [
    backendSource,
    aggregationField,
    entitySelectStep,
    entityVersionStep,
    promptId,
    workflowId,
    metadataKey,
    hideSignalMetadata,
    prefixOverride,
    serializedFilterGroup,
  ].join("|");
  const previousFiltersKeyRef = useRef(suggestionFiltersKey);
  const baseEnabled = Boolean(userToken) && workspaceId > 0 && enabled && backendSource !== null;
  const requiresField = backendSource === BACKEND_SOURCE_BY_TYPE[SuggestionSourceType.Aggregation];
  const queryEnabled = baseEnabled && (requiresField ? Boolean(aggregationField) : true);

  const suggestionsQuery = useQuery<StructuredSearchSuggestionsResponse>({
    queryKey: [
      queryKeys.structuredSearchSuggestions,
      workspaceId,
      schema.id,
      suggestionFiltersKey,
      prefix,
      userToken,
    ],
    queryFn: async () => {
      if (!userToken || !workspaceId || !backendSource) {
        return { values: [] };
      }

      const url = ENDPOINTS.get_structured_search_suggestions(workspaceId, {
        searchType: schema.id,
        source: backendSource,
        field: requiresField ? aggregationField : undefined,
        prefix: prefix || undefined,
        promptId,
        workflowId,
        metadataKey,
        filterGroup: serializedFilterGroup,
        hideSignalMetadata,
      });
      const response = await fetch(url, {
        method: "GET",
        headers: jsonAuthHeaders(userToken),
      });

      return handleResponse(response);
    },
    enabled: queryEnabled,
    retry: QUERY_RETRY_COUNT,
    staleTime: QUERY_STALE_MS,
    // Keep list while typing (prefix-only). Clear when suggestionFiltersKey changes.
    keepPreviousData: true,
  });

  if (!suggestionsQuery.isPreviousData) {
    previousFiltersKeyRef.current = suggestionFiltersKey;
  }

  if (!backendSource) {
    return { options: null, isLoading: false };
  }

  if (!queryEnabled) {
    return { options: null, isLoading: false };
  }

  const isStaleFilterContext =
    suggestionsQuery.isPreviousData && previousFiltersKeyRef.current !== suggestionFiltersKey;

  if (suggestionsQuery.data === undefined || isStaleFilterContext) {
    return {
      options: null,
      isLoading: suggestionsQuery.fetchStatus === "fetching",
    };
  }

  const options = suggestionsQuery.data.values.map(item => ({
    value: item.value,
    count: item.count,
    prompt_name: item.prompt_name ?? undefined,
    label: item.label ?? undefined,
  }));

  return {
    options,
    isLoading: suggestionsQuery.fetchStatus === "fetching",
  };
};
